import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { RefreshCw, Camera, Clock, Users, X, ChevronLeft, ChevronRight, CalendarIcon } from 'lucide-react'
import { formatDateTime } from '@/lib/dateTime'
import { SightingDetailDialog } from '@/components/SightingDetailDialog'
import { format, parse, startOfDay, endOfDay } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import type { Device } from '@/types'

interface Face {
  personId: string | null
  personName: string
  classification: string
  classificationId: string
  bbox: { x: number; y: number; width: number; height: number }
  isNewFace: boolean
  isUnmatched?: boolean
  similarity: number | null
  detScore?: number | null
  qualityScore?: number | null
}

interface Sighting {
  id: string
  cameraId: string
  deviceId: string
  deviceName: string
  cameraName?: string
  timestamp: string
  frameUrl: string
  faces: Face[]
  metadata: {
    totalFaces: number
    alertSent: boolean
    hasNewFaces: boolean
    hasUnmatchedFaces?: boolean
  }
}

interface ClassificationOption {
  id: string
  color: string
  displayName: string
}

interface SightingsTabProps {
  groupId: string
  onNavigateToPerson?: (personId: string) => void
  filterPersonId?: string | null
  filterPersonName?: string | null
  onClearPersonFilter?: () => void
  urlSightingId?: string | null
}

const PERIOD_VALUES = ['30m', '2h', '24h', '7d', '30d', 'all', 'custom'] as const

const DATE_FORMAT = 'yyyy-MM-dd'

const PAGE_SIZE = 20

export function SightingsTab({ groupId, onNavigateToPerson, filterPersonId, filterPersonName, onClearPersonFilter, urlSightingId }: SightingsTabProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  // URL-synced filter state
  const period = searchParams.get('period') || '24h'
  const selectedCamera = searchParams.get('camera') || 'all'
  const selectedClassification = searchParams.get('classification') || 'all'
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10))
  const cursorFromUrl = searchParams.get('cursor') || ''

  // Custom date range from URL
  const fromParam = searchParams.get('from')
  const toParam = searchParams.get('to')
  const dateRange: DateRange | undefined = useMemo(() => {
    if (period !== 'custom' || !fromParam) return undefined
    try {
      const from = parse(fromParam, DATE_FORMAT, new Date())
      const to = toParam ? parse(toParam, DATE_FORMAT, new Date()) : from
      return { from, to }
    } catch {
      return undefined
    }
  }, [period, fromParam, toParam])

  const periodOptions = useMemo(() => PERIOD_VALUES.map(value => ({
    value,
    label: t(`sightings.periods.${value}`),
  })), [t])

  // Data state
  const [sightings, setSightings] = useState<Sighting[]>([])
  // totalCount is only set when the backend takes the legacy face_events code path
  // (person/classification filtered) — the standard sightings list switched to cursor
  // pagination to avoid full-collection scans, so we no longer have a count there.
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  // In-memory cursor stack for Previous navigation. Empty string represents the first page.
  const [cursorStack, setCursorStack] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Reference data
  const [classifications, setClassifications] = useState<ClassificationOption[]>([])
  const [classificationMap, setClassificationMap] = useState<Map<string, ClassificationOption>>(new Map())
  const [devices, setDevices] = useState<Device[]>([])

  // Flatten cameras from all devices for the camera filter dropdown
  const cameraOptions = useMemo(() => {
    const cameras: { cameraId: string; cameraName: string; deviceName: string }[] = []
    for (const device of devices) {
      if (device.cameras && device.cameras.length > 0) {
        for (const cam of device.cameras) {
          cameras.push({
            cameraId: cam.id,
            cameraName: cam.name,
            deviceName: device.name,
          })
        }
      }
    }
    return cameras
  }, [devices])

  // Canvas rendering
  const canvasRefs = useRef<Map<string, HTMLCanvasElement>>(new Map())
  const imageCache = useRef<Map<string, HTMLImageElement>>(new Map())

  // When a multi-page jump has populated state ahead of a URL change, skip one fetch.
  const skipNextLoadRef = useRef(false)

  const totalPages = totalCount !== null ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : 1
  const isPageMode = totalCount !== null
  const cursorPageIndex = cursorStack.length + 1
  const canGoPrev = isPageMode ? currentPage > 1 : cursorStack.length > 0
  const canGoNext = isPageMode ? currentPage < totalPages : hasMore

  // Sighting detail dialog - driven by URL
  const detailDialogOpen = !!urlSightingId
  const selectedSightingId = urlSightingId || null

  // ─── URL param helpers ───

  const setFilterParam = useCallback((key: string, value: string, defaultValue: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value === defaultValue) {
        next.delete(key)
      } else {
        next.set(key, value)
      }
      // Reset page/cursor when filters change
      if (key !== 'page' && key !== 'cursor') {
        next.delete('page')
        next.delete('cursor')
      }
      return next
    })
    setCursorStack([])
  }, [setSearchParams])

  const handlePeriodChange = (value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value === '24h') {
        next.delete('period')
      } else {
        next.set('period', value)
      }
      // Clean up date range params when switching to a preset
      if (value !== 'custom') {
        next.delete('from')
        next.delete('to')
      }
      next.delete('page')
      next.delete('cursor')
      return next
    })
    setCursorStack([])
  }

  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (!range?.from) return
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('period', 'custom')
      next.set('from', format(range.from!, DATE_FORMAT))
      if (range.to) {
        next.set('to', format(range.to, DATE_FORMAT))
      } else {
        next.delete('to')
      }
      next.delete('page')
      next.delete('cursor')
      return next
    })
    setCursorStack([])
  }

  const handleCameraChange = (value: string) => setFilterParam('camera', value, 'all')
  const handleClassificationChange = (value: string) => setFilterParam('classification', value, 'all')

  const setPage = useCallback((newPage: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (newPage <= 1) {
        next.delete('page')
      } else {
        next.set('page', String(newPage))
      }
      return next
    })
  }, [setSearchParams])

  const goNext = useCallback(() => {
    if (isPageMode) {
      setPage(currentPage + 1)
      return
    }
    if (!hasMore || !nextCursor) return
    setCursorStack(prev => [...prev, cursorFromUrl])
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('cursor', nextCursor)
      return next
    })
  }, [isPageMode, currentPage, setPage, hasMore, nextCursor, cursorFromUrl, setSearchParams])

  const goPrev = useCallback(() => {
    if (isPageMode) {
      setPage(Math.max(1, currentPage - 1))
      return
    }
    if (cursorStack.length === 0) return
    const prevCursor = cursorStack[cursorStack.length - 1]
    setCursorStack(prev => prev.slice(0, -1))
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (prevCursor) {
        next.set('cursor', prevCursor)
      } else {
        next.delete('cursor')
      }
      return next
    })
  }, [isPageMode, currentPage, setPage, cursorStack, setSearchParams])

  // Jump back k pages: oldest popped entry is the cursor for the target page.
  const goBackPages = useCallback((k: number) => {
    if (k <= 0 || cursorStack.length === 0) return
    const popCount = Math.min(k, cursorStack.length)
    const targetCursor = cursorStack[cursorStack.length - popCount]
    setCursorStack(prev => prev.slice(0, -popCount))
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (targetCursor) {
        next.set('cursor', targetCursor)
      } else {
        next.delete('cursor')
      }
      return next
    })
  }, [cursorStack, setSearchParams])

  // Jump forward k pages (1–4). Backend reads (k+1) pages worth of docs and returns
  // boundary cursors so Prev still walks one page at a time.
  const goForwardPages = useCallback(async (k: number) => {
    if (k <= 0 || isPageMode || !hasMore) return
    try {
      setLoading(true)
      setError(null)
      const listSightingsForUser = httpsCallable(functions, 'listSightingsForUser')
      const params: Record<string, any> = {
        groupId,
        cameraId: selectedCamera !== 'all' ? selectedCamera : undefined,
        classificationId: selectedClassification !== 'all' ? selectedClassification : undefined,
        personId: filterPersonId || undefined,
        page: currentPage,
        pageSize: PAGE_SIZE,
        skipPages: k,
      }
      if (cursorFromUrl) params.cursor = cursorFromUrl
      if (period === 'custom' && dateRange?.from) {
        params.startDate = startOfDay(dateRange.from).toISOString()
        params.endDate = endOfDay(dateRange.to || dateRange.from).toISOString()
        params.period = 'all'
      } else {
        params.period = period
      }
      const result = await listSightingsForUser(params)
      const data = result.data as {
        success: boolean
        sightings: Sighting[]
        nextCursor?: string | null
        hasMore?: boolean
        skippedCursors?: string[]
      }
      if (!data.success) return
      const cursors = data.skippedCursors || []
      if (cursors.length === 0) return
      const toPush = [cursorFromUrl, ...cursors.slice(0, -1)]
      setSightings(data.sightings)
      setTotalCount(null)
      setHasMore(!!data.hasMore)
      setNextCursor(data.nextCursor || null)
      setCursorStack(prev => [...prev, ...toPush])
      skipNextLoadRef.current = true
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.set('cursor', cursors[cursors.length - 1])
        return next
      })
    } catch (err) {
      console.error('Failed to jump ahead:', err)
      setError(t('sightings.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }, [isPageMode, hasMore, groupId, selectedCamera, selectedClassification, filterPersonId, currentPage, cursorFromUrl, period, dateRange, setSearchParams, t])

  const handleSightingClick = (sightingId: string) => {
    navigate(`/sightings/${sightingId}${location.search}`)
  }

  // ─── Load reference data ───

  useEffect(() => {
    const loadClassifications = async () => {
      try {
        const getClassifications = httpsCallable(functions, 'getClassifications')
        const result = await getClassifications({ groupId })
        const data = result.data as { success: boolean; classifications: ClassificationOption[] }
        if (data.success) {
          setClassifications(data.classifications)
          const map = new Map<string, ClassificationOption>()
          data.classifications.forEach(c => map.set(c.id, c))
          setClassificationMap(map)
        }
      } catch (err) {
        console.error('Failed to load classifications:', err)
      }
    }

    const loadDevices = async () => {
      try {
        const getUserDevices = httpsCallable(functions, 'getUserDevices')
        const result = await getUserDevices({})
        const data = result.data as { success: boolean; devices: Device[] }
        if (data.success) {
          setDevices(data.devices.filter(d => d.groupId === groupId))
        }
      } catch (err) {
        console.error('Failed to load devices:', err)
      }
    }

    loadClassifications()
    loadDevices()
  }, [groupId])

  // ─── Load sightings ───

  const loadSightings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const listSightingsForUser = httpsCallable(functions, 'listSightingsForUser')

      // Build request params - use startDate/endDate for custom range.
      // Send both `cursor` and `page`: backend Path C uses cursor (cheap), Paths A/B
      // (person/classification filtered) use page (legacy face_events scan path).
      const params: Record<string, any> = {
        groupId,
        cameraId: selectedCamera !== 'all' ? selectedCamera : undefined,
        classificationId: selectedClassification !== 'all' ? selectedClassification : undefined,
        personId: filterPersonId || undefined,
        page: currentPage,
        pageSize: PAGE_SIZE,
      }
      if (cursorFromUrl) {
        params.cursor = cursorFromUrl
      }

      if (period === 'custom' && dateRange?.from) {
        params.startDate = startOfDay(dateRange.from).toISOString()
        params.endDate = endOfDay(dateRange.to || dateRange.from).toISOString()
        params.period = 'all' // Tell backend to skip period-based filtering
      } else {
        params.period = period
      }

      const result = await listSightingsForUser(params)

      const data = result.data as {
        success: boolean
        sightings: Sighting[]
        totalCount?: number
        page?: number
        pageSize: number
        nextCursor?: string | null
        hasMore?: boolean
        skippedCursors?: string[]
      }

      if (data.success) {
        setSightings(data.sightings)
        // Backend tells us which mode it used by what fields it returns.
        if (typeof data.totalCount === 'number') {
          setTotalCount(data.totalCount)
          setHasMore(false)
          setNextCursor(null)
        } else {
          setTotalCount(null)
          setHasMore(!!data.hasMore)
          setNextCursor(data.nextCursor || null)
        }
      }
    } catch (err) {
      console.error('Failed to load sightings:', err)
      setError(t('sightings.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }, [groupId, period, selectedCamera, selectedClassification, filterPersonId, currentPage, cursorFromUrl, dateRange])

  useEffect(() => {
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false
      return
    }
    loadSightings()
  }, [loadSightings])

  // ─── Canvas annotation rendering ───

  useEffect(() => {
    sightings.forEach(sighting => drawAnnotations(sighting))
  }, [sightings, classificationMap])

  const drawAnnotations = (sighting: Sighting) => {
    const canvas = canvasRefs.current.get(sighting.id)
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const cachedImg = imageCache.current.get(sighting.frameUrl)
    if (cachedImg && cachedImg.complete) {
      renderAnnotatedFrame(ctx, canvas, cachedImg, sighting.faces)
    } else {
      const newImg = new Image()
      newImg.crossOrigin = 'anonymous'
      newImg.onload = () => {
        imageCache.current.set(sighting.frameUrl, newImg)
        renderAnnotatedFrame(ctx, canvas, newImg, sighting.faces)
      }
      newImg.onerror = () => {
        console.error('Failed to load image:', sighting.frameUrl)
      }
      newImg.src = sighting.frameUrl
    }
  }

  const renderAnnotatedFrame = (
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    img: HTMLImageElement,
    faces: Face[]
  ) => {
    canvas.width = img.width
    canvas.height = img.height
    ctx.drawImage(img, 0, 0)

    faces.forEach(face => {
      if (face.isUnmatched) {
        ctx.strokeStyle = '#888888'
        ctx.lineWidth = 2
        ctx.setLineDash([6, 4])
        ctx.strokeRect(face.bbox.x, face.bbox.y, face.bbox.width, face.bbox.height)
        ctx.setLineDash([])

        const label = 'Unidentified'
        ctx.font = 'italic bold 14px sans-serif'
        const textWidth = ctx.measureText(label).width
        ctx.fillStyle = '#888888'
        ctx.fillRect(face.bbox.x, face.bbox.y - 22, textWidth + 8, 22)
        ctx.fillStyle = '#ffffff'
        ctx.fillText(label, face.bbox.x + 4, face.bbox.y - 6)
        ctx.font = 'bold 14px sans-serif'
      } else {
        const classification = classificationMap.get(face.classificationId)
        const color = classification?.color || '#999999'

        ctx.strokeStyle = color
        ctx.lineWidth = 3
        ctx.strokeRect(face.bbox.x, face.bbox.y, face.bbox.width, face.bbox.height)

        const label = face.personName || 'Unknown'
        ctx.font = 'bold 14px sans-serif'
        const textWidth = ctx.measureText(label).width
        ctx.fillStyle = color
        ctx.fillRect(face.bbox.x, face.bbox.y - 22, textWidth + 8, 22)
        ctx.fillStyle = '#ffffff'
        ctx.fillText(label, face.bbox.x + 4, face.bbox.y - 6)
      }
    })
  }

  const handlePersonClick = (personId: string) => {
    navigate(`/persons/${personId}`)
  }

  // ─── Pagination page numbers (same as PersonsCatalog) ───

  const pageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis')[] = []
    if (totalPages <= 9) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      const visible = new Set<number>()
      visible.add(1)
      visible.add(totalPages)
      for (let i = currentPage - 2; i <= currentPage + 2; i++) {
        if (i >= 1 && i <= totalPages) visible.add(i)
      }
      const sorted = Array.from(visible).sort((a, b) => a - b)
      for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) pages.push('ellipsis')
        pages.push(sorted[i])
      }
    }
    return pages
  }, [currentPage, totalPages])

  // ─── Render ───

  if (loading && sightings.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Alert variant="destructive" className="mb-4 text-left">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button variant="outline" onClick={() => loadSightings()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common.retry')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header \u2014 serif title + gold rule + count (matches the Devices tab) */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-[28px] font-medium leading-[1.05] tracking-[-0.005em] text-ink">
            {t('sightings.title')}
          </h2>
          <hr className="gold-rule mt-3" />
          <p className="mt-3 text-[13px] leading-[1.55] text-ink-soft">
            {isPageMode
              ? t('sightings.count', { count: totalCount || 0 })
              : t('sightings.countOnPage', { count: sightings.length, defaultValue: '{{count}} sightings on this page' })}
            {isPageMode && totalPages > 1 && ` \u00B7 ${t('sightings.pageOf', { current: currentPage, total: totalPages })}`}
            {!isPageMode && (canGoPrev || canGoNext) && ` \u00B7 ${t('sightings.pageNumber', { page: cursorPageIndex, defaultValue: 'Page {{page}}' })}`}
          </p>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => loadSightings()}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <Select value={period} onValueChange={handlePeriodChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {periodOptions.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {period === 'custom' && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start text-left font-normal min-w-[240px]">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateRange?.from ? (
                  dateRange.to && dateRange.to.getTime() !== dateRange.from.getTime() ? (
                    <>
                      {format(dateRange.from, 'MMM d, yyyy')} &ndash; {format(dateRange.to, 'MMM d, yyyy')}
                    </>
                  ) : (
                    format(dateRange.from, 'MMM d, yyyy')
                  )
                ) : (
                  <span className="text-muted-foreground">{t('sightings.pickDateRange')}</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={handleDateRangeChange}
                numberOfMonths={2}
                disabled={{ after: new Date() }}
              />
            </PopoverContent>
          </Popover>
        )}

        {cameraOptions.length > 0 && (
          <Select value={selectedCamera} onValueChange={handleCameraChange}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder={t('sightings.allCameras')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('sightings.allCameras')}</SelectItem>
              {cameraOptions.map(cam => (
                <SelectItem key={cam.cameraId} value={cam.cameraId}>
                  <div className="flex items-center gap-2">
                    <Camera className="h-3 w-3" />
                    <span>{cam.cameraName}</span>
                    <span className="text-muted-foreground text-xs">({cam.deviceName})</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={selectedClassification} onValueChange={handleClassificationChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('persons.allClassifications')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('persons.allClassifications')}</SelectItem>
            {classifications.map(c => (
              <SelectItem key={c.id} value={c.id}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: c.color }}
                  />
                  {c.displayName}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Person filter indicator */}
      {filterPersonId && filterPersonName && (
        <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">
            {t('sightings.showingFor')} <strong>{filterPersonName}</strong>
          </span>
          {onClearPersonFilter && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-7 px-2"
              onClick={onClearPersonFilter}
            >
              <X className="h-4 w-4 mr-1" />
              {t('sightings.clearFilter')}
            </Button>
          )}
        </div>
      )}

      {/* Sightings grid */}
      {sightings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t('sightings.noSightings')}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sightings.map(sighting => (
              <Card key={sighting.id} className="overflow-hidden">
                <div className="relative">
                  <canvas
                    ref={el => {
                      if (el) canvasRefs.current.set(sighting.id, el)
                    }}
                    className="w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => handleSightingClick(sighting.id)}
                  />

                  {sighting.metadata.alertSent && (
                    <Badge
                      variant="destructive"
                      className="absolute top-2 right-2"
                    >
                      {t('sightings.alert')}
                    </Badge>
                  )}
                </div>

                <CardContent className="p-4 space-y-3">
                  {/* Timestamp and camera */}
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{formatDateTime(sighting.timestamp)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Camera className="h-3 w-3" />
                      <span>{sighting.cameraName || sighting.deviceName}</span>
                    </div>
                  </div>

                  {/* Faces count */}
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {t('sightings.people', { count: sighting.faces.length })}
                    </span>
                    {sighting.metadata.hasNewFaces && (
                      <Badge variant="secondary" className="ml-auto">
                        {t('sightings.newFace')}
                      </Badge>
                    )}
                    {sighting.metadata.hasUnmatchedFaces && (
                      <Badge variant="outline" className="ml-auto text-muted-foreground">
                        {t('sightings.unidentified')}
                      </Badge>
                    )}
                  </div>

                  {/* Person list */}
                  <div className="space-y-1">
                    {sighting.faces.map((face, idx) => {
                      if (face.isUnmatched || !face.personId) {
                        return (
                          <div
                            key={idx}
                            className="w-full flex items-center justify-between p-2 rounded text-left"
                          >
                            <span className="font-medium truncate italic text-muted-foreground">
                              {t('sightings.unidentified')}
                            </span>
                            <Badge variant="outline" className="ml-2 shrink-0 text-muted-foreground">
                              {t('sightings.noMatch')}
                            </Badge>
                          </div>
                        )
                      }
                      const classification = classificationMap.get(face.classificationId)
                      return (
                        <button
                          key={idx}
                          onClick={() => handlePersonClick(face.personId!)}
                          className="w-full flex items-center justify-between p-2 rounded hover:bg-muted transition-colors text-left"
                        >
                          <span className="font-medium truncate">
                            {face.personName || t('common.unknown')}
                          </span>
                          {classification && (
                            <Badge
                              style={{
                                backgroundColor: classification.color,
                                color: '#ffffff'
                              }}
                              className="ml-2 shrink-0"
                            >
                              {classification.displayName}
                            </Badge>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {isPageMode && totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage <= 1 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
                {t('common.previous')}
              </Button>
              {pageNumbers.map((p, i) =>
                p === 'ellipsis' ? (
                  <span key={`ellipsis-${i}`} className="px-2 text-muted-foreground">...</span>
                ) : (
                  <Button
                    key={p}
                    variant={p === currentPage ? 'default' : 'outline'}
                    size="sm"
                    className="min-w-[36px]"
                    onClick={() => setPage(p)}
                    disabled={loading}
                  >
                    {p}
                  </Button>
                )
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= totalPages || loading}
              >
                {t('common.next')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          {!isPageMode && (canGoPrev || canGoNext) && (
            <div className="flex items-center justify-center gap-1 pt-4 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={goPrev}
                disabled={!canGoPrev || loading}
              >
                <ChevronLeft className="h-4 w-4" />
                {t('common.previous')}
              </Button>
              {Array.from({ length: Math.min(cursorStack.length, 4) }, (_, i) => {
                const offset = Math.min(cursorStack.length, 4) - i
                const pageNum = cursorPageIndex - offset
                return (
                  <Button
                    key={`back-${offset}`}
                    variant="outline"
                    size="sm"
                    className="min-w-[36px]"
                    onClick={() => goBackPages(offset)}
                    disabled={loading}
                  >
                    {pageNum}
                  </Button>
                )
              })}
              <Button variant="default" size="sm" className="min-w-[36px]" disabled>
                {cursorPageIndex}
              </Button>
              {hasMore && Array.from({ length: 4 }, (_, i) => i + 1).map(offset => (
                <Button
                  key={`fwd-${offset}`}
                  variant="outline"
                  size="sm"
                  className="min-w-[36px]"
                  onClick={() => offset === 1 ? goNext() : goForwardPages(offset)}
                  disabled={loading}
                >
                  {cursorPageIndex + offset}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={goNext}
                disabled={!canGoNext || loading}
              >
                {t('common.next')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Sighting Detail Dialog */}
      <SightingDetailDialog
        open={detailDialogOpen}
        onOpenChange={(open) => {
          if (!open) navigate(`/sightings${location.search}`)
        }}
        sightingId={selectedSightingId}
        groupId={groupId}
        onNavigateToPerson={onNavigateToPerson}
      />
    </div>
  )
}
